import React, { useCallback, useEffect, useMemo } from 'react';
import { LaunchBulkEditTags } from 'src/components/common/BulkActions/BulkEditTagsAction';
import { useBulkEditTagsPermissions } from 'src/components/common/BulkActions/BulkEditTagsAction/useBulkEditTagsPermissions';
import {
  IPosEntity,
  useActivePosEntityContext,
} from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useCommandMenuContext } from 'src/contexts/CommandMenuContext/CommandMenuContext';
import { DialogId } from 'src/contexts/DialogContext/DialogContext';
import { useDialog } from 'src/contexts/DialogContext/useDialog';
import {
  BulkEditTagAction,
  BulkEditTagDialog,
} from 'src/dialogs/BulkEdits/BulkEditTagDialog';
import { useEventCallback } from 'src/hooks/useEventCallback';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  BulkEditListingClient,
  BulkEditPreviewWithDetails,
  CommandOptionType,
  Event,
  ListingQuery,
} from 'src/WebApiController';

import { INVENTORY_BULK_UPDATE_TAGS_UPDATE_KEY } from '../../InventoryActionDropdown.constants';
import { BulkActionItem } from '../types';

export const useBulkEditTagsAction = (
  filterQueryWithEventIds: ListingQuery,
  setIsLoading: (v: boolean) => void,
  isLoading?: boolean,
  event?: Event
) => {
  const { registerCommand, unregisterCommand } = useCommandMenuContext();
  const { hasEditTagsPermissions } = useBulkEditTagsPermissions({
    entityType: ActionOutboxEntityType.Listing,
  });

  const { activeAccountWebClientConfig } = useAppContext();
  const { setActivePosEntity } = useActivePosEntityContext<IPosEntity>();
  const updateKey = event
    ? `listingTags-event-${event.virtId}`
    : INVENTORY_BULK_UPDATE_TAGS_UPDATE_KEY;
  const {
    eventsExpansion: { setListItemExpansion, refreshExpandedListItems },
  } = useCatalogDataContext();
  const { showErrorDialog } = useClientLogContext();

  const {
    openDialog: openBulkEditTagDialog,
    closeDialog: closeBulkEditTagDialog,
  } = useDialog(DialogId.BulkEditTags, BulkEditTagDialog);

  const onSubmitEditTags = useCallback(
    async (
      action: BulkEditTagAction,
      tagKey: string | null,
      tagValue?: string | undefined,
      supportBackgroundProcess?: boolean,
      onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
      preview?: BulkEditPreviewWithDetails
    ) => {
      if (activeAccountWebClientConfig.activeAccountId == null) {
        return null;
      }
      setIsLoading(true);
      if (onPreviewReceived) {
        await tryInvokeApi(
          async () => {
            const preview = await new BulkEditListingClient(
              activeAccountWebClientConfig
            ).bulkUpdateTagsPreview(filterQueryWithEventIds);

            onPreviewReceived(preview);
          },
          (error) => {
            showErrorDialog('bulkUpdateTagsPreview', error, {
              trackErrorData: { filterQueryWithEventIds },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      } else if (tagKey) {
        await tryInvokeApi(
          async () => {
            const listingClient = new BulkEditListingClient(
              activeAccountWebClientConfig
            );

            const succeeded =
              action === BulkEditTagAction.Update
                ? await listingClient.bulkUpdateTags(
                    preview!.preview,
                    tagKey,
                    tagValue,
                    updateKey,
                    supportBackgroundProcess
                  )
                : await listingClient.bulkRemoveTags(
                    preview!.preview,
                    tagKey,
                    updateKey,
                    supportBackgroundProcess
                  );

            if (!supportBackgroundProcess) {
              if (succeeded) {
                // Invalidate the activePosEntity, so that when clicking on listingDetails
                // we force re-loading the listing and newest pricing settings can be displayed
                setActivePosEntity(0);
                await refreshExpandedListItems();
              }

              closeBulkEditTagDialog();
            }
          },
          (error) => {
            showErrorDialog('onSubmitEditTags', error, {
              trackErrorData: { tagKey, tagValue, preview },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      }
    },
    [
      activeAccountWebClientConfig,
      setIsLoading,
      filterQueryWithEventIds,
      showErrorDialog,
      updateKey,
      closeBulkEditTagDialog,
      setActivePosEntity,
      refreshExpandedListItems,
    ]
  );

  const onChangeTagClick = useCallback(
    (e: React.MouseEvent, editTagAction: BulkEditTagAction) => {
      e.preventDefault();
      e.stopPropagation();
      if (event) {
        setListItemExpansion(true, [event.virtId]);
      }

      openBulkEditTagDialog({
        updateKey: updateKey,
        action: editTagAction,
        onOkay: onSubmitEditTags,
        entityType: ActionOutboxEntityType.Listing,
        onClosed: () => {
          setIsLoading(false);
        },
        onCancel: closeBulkEditTagDialog,
      });
    },
    [
      closeBulkEditTagDialog,
      event,
      onSubmitEditTags,
      openBulkEditTagDialog,
      setIsLoading,
      setListItemExpansion,
      updateKey,
    ]
  );

  const openAddTagDialogStableRef = useEventCallback(() => {
    openBulkEditTagDialog({
      updateKey: updateKey,
      action: BulkEditTagAction.Update,
      onOkay: onSubmitEditTags,
      entityType: ActionOutboxEntityType.Listing,
      onClosed: () => {
        setIsLoading(false);
      },
      onCancel: closeBulkEditTagDialog,
    });
  });

  const openRemoveTagDialogStableRef = useEventCallback(() => {
    openBulkEditTagDialog({
      updateKey: updateKey,
      action: BulkEditTagAction.Remove,
      onOkay: onSubmitEditTags,
      entityType: ActionOutboxEntityType.Listing,
      onClosed: () => {
        setIsLoading(false);
      },
      onCancel: closeBulkEditTagDialog,
    });
  });

  useEffect(() => {
    registerCommand({
      command: {
        id: CommandOptionType.ListingBulkEditAddTags,
        onSelect: openAddTagDialogStableRef,
      },
      disabled: !hasEditTagsPermissions,
    });

    registerCommand({
      command: {
        id: CommandOptionType.ListingBulkEditRemoveTags,
        onSelect: openRemoveTagDialogStableRef,
      },
      disabled: !hasEditTagsPermissions,
    });

    return () => {
      unregisterCommand({
        commandId: CommandOptionType.ListingBulkEditAddTags,
        disabled: !hasEditTagsPermissions,
      });
      unregisterCommand({
        commandId: CommandOptionType.ListingBulkEditRemoveTags,
        disabled: !hasEditTagsPermissions,
      });
    };
  }, [
    hasEditTagsPermissions,
    openAddTagDialogStableRef,
    openRemoveTagDialogStableRef,
    registerCommand,
    unregisterCommand,
  ]);

  const onClick = useCallback(
    (e: React.MouseEvent, action: BulkEditTagAction) => {
      e.preventDefault();
      e.stopPropagation();
      if (event) {
        setListItemExpansion(true, [event.virtId]);
      }
      openBulkEditTagDialog({
        updateKey: updateKey,
        action: action,
        onOkay: onSubmitEditTags,
        entityType: ActionOutboxEntityType.Listing,
        onClosed: () => {
          setIsLoading(false);
        },
        onCancel: closeBulkEditTagDialog,
      });
    },
    [
      closeBulkEditTagDialog,
      event,
      onSubmitEditTags,
      openBulkEditTagDialog,
      setIsLoading,
      setListItemExpansion,
      updateKey,
    ]
  );

  const actionItems = useMemo<BulkActionItem[]>(() => {
    if (!hasEditTagsPermissions) {
      return [];
    }

    return [
      {
        title: ContentId.UpdateTagValues,
        disabled: isLoading,
        onClick: (e: React.MouseEvent) => onClick(e, BulkEditTagAction.Update),
      },
      {
        title: ContentId.RemoveTagValues,
        disabled: isLoading,
        onClick: (e: React.MouseEvent) => onClick(e, BulkEditTagAction.Remove),
      },
    ];
  }, [hasEditTagsPermissions, isLoading, onClick]);

  return {
    dropDown: (
      <LaunchBulkEditTags
        key="EditTags"
        onClick={onClick}
        disabled={isLoading}
        entityType={ActionOutboxEntityType.Listing}
      />
    ),
    actionItems,
  };
};
